import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { PerClassBarRow } from '@/type/kpi-dashboard.types';
import { getAxisLabelStep } from '@/utils/kpi-dashboard.utils';

interface PerClassBarChartProps {
  rows: PerClassBarRow[];
  /** Upper bound of the value axis. */
  axisMax: number;
  ticks: number[];
  formatValue: (value: number) => string;
}

/** The hovered bar's tooltip, anchored in viewport coordinates. */
interface HoveredBar {
  row: PerClassBarRow;
  x: number;
  y: number;
  isFlipped: boolean;
}

const ROW_HEIGHT = 27;
const BAR_HEIGHT = 16;
/**
 * Width of the left gutter holding the class names. Sized to fit about 95% of
 * them outright; the handful of longer ones truncate and are readable in full
 * from the hover tooltip.
 */
const LABEL_WIDTH = 220;
/** Past this fill ratio the tooltip flips inside the bar so it stays visible. */
const TOOLTIP_FLIP_RATIO = 0.6;
/** Keeps the tooltip clear of the viewport edges when a row sits near one. */
const VIEWPORT_MARGIN = 8;
/** Roughly the height of the two-line tooltip, used only for the clamp. */
const TOOLTIP_HEIGHT = 52;

const getAxisLabelStyle = (
  index: number,
  tickCount: number,
  offsetPercent: number,
): React.CSSProperties => {
  if (index === 0) return { left: 0 };
  if (index === tickCount - 1) return { right: 0 };

  return { left: `${offsetPercent}%`, transform: 'translateX(-50%)' };
};

export const PerClassBarChart = ({
  rows,
  axisMax,
  ticks,
  formatValue,
}: PerClassBarChartProps) => {
  /**
   * The rows scroll inside a clipping container, so an absolutely positioned
   * tooltip would be cut off whenever its row sits near the visible edge. It is
   * rendered into the body instead, above every container.
   */
  const [hoveredBar, setHoveredBar] = useState<HoveredBar | null>(null);

  /** Width of the value axis, used to thin its labels once they stop fitting. */
  const axisRef = useRef<HTMLDivElement>(null);
  const [axisWidth, setAxisWidth] = useState(0);

  useEffect(() => {
    const axis = axisRef.current;

    if (!axis || typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver(([entry]) =>
      setAxisWidth(entry?.contentRect?.width ?? 0),
    );

    observer.observe(axis);

    return () => observer.disconnect();
  }, []);

  const tickStep = getAxisLabelStep((ticks ?? []).length, axisWidth);

  /**
   * Bound to the whole row, class name included, so hovering the left gutter
   * shows the tooltip just as hovering the bar does. The bar track is the row
   * minus that fixed-width gutter, which is where the tooltip anchors.
   */
  const handleRowEnter = (
    event: React.MouseEvent<HTMLDivElement>,
    row: PerClassBarRow,
    ratio: number,
  ) => {
    const rowRect = event.currentTarget.getBoundingClientRect();
    const trackWidth = Math.max(rowRect.width - LABEL_WIDTH, 0);
    const barEnd = rowRect.left + LABEL_WIDTH + trackWidth * ratio;

    setHoveredBar({
      row,
      x: barEnd,
      y: Math.min(
        Math.max(
          rowRect.top + rowRect.height / 2,
          TOOLTIP_HEIGHT / 2 + VIEWPORT_MARGIN,
        ),
        window.innerHeight - TOOLTIP_HEIGHT / 2 - VIEWPORT_MARGIN,
      ),
      isFlipped: ratio > TOOLTIP_FLIP_RATIO,
    });
  };

  return (
    <div className="w-full">
      <div
        className="overflow-hidden overflow-y-auto max-h-[58.1vh]"
        tabIndex={0}
        onScroll={() => setHoveredBar(null)}
      >
        <div className="relative">
          <div
            className="pointer-events-none absolute inset-y-0 right-0"
            style={{ left: LABEL_WIDTH }}
          >
            {ticks.map((tick) => (
              <div
                key={tick}
                className="absolute inset-y-0 w-px bg-gray-DBDFE9"
                style={{ left: `${(tick / axisMax) * 100}%` }}
              />
            ))}
          </div>

          {(rows ?? []).map((row) => {
            const ratio = axisMax ? (row?.value ?? 0) / axisMax : 0;

            return (
              <div
                key={row?.id}
                className="group flex items-center"
                style={{ height: ROW_HEIGHT }}
                onMouseEnter={(event) => handleRowEnter(event, row, ratio)}
                onMouseLeave={() => setHoveredBar(null)}
              >
                <div
                  className="shrink-0 truncate pr-2 text-right text-[12px] leading-none text-gray-78829D"
                  style={{ width: LABEL_WIDTH }}
                >
                  {row?.label}
                </div>

                <div className="relative h-full min-w-0 flex-1">
                  <div
                    className="absolute top-1/2 left-0 -translate-y-1/2 rounded-r-[4px] transition-opacity group-hover:opacity-80"
                    style={{
                      width: `${ratio * 100}%`,
                      height: BAR_HEIGHT,
                      background: row?.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="flex" style={{ paddingLeft: LABEL_WIDTH }}>
        <div
          className="relative h-6 flex-1 border-t border-gray-DBDFE9"
          ref={axisRef}
        >
          {(ticks ?? []).map((tick, index) => {
            if (index % tickStep !== 0) return null;

            // The outer labels align to the axis ends rather than to their tick,
            // so half of them cannot hang off the card.
            const style = getAxisLabelStyle(
              index,
              (ticks ?? []).length,
              axisMax ? (tick / axisMax) * 100 : 0,
            );

            return (
              <span
                key={tick}
                className="absolute top-1.5 text-2xs leading-none text-gray-78829D tabular-nums"
                style={style}
              >
                {formatValue(tick)}
              </span>
            );
          })}
        </div>
      </div>

      {hoveredBar &&
        createPortal(
          <div
            className="pointer-events-none fixed z-[100] whitespace-nowrap rounded-lg border border-gray-DBDFE9 bg-white px-2.5 py-1.5 text-[12.75px] shadow-default"
            style={{
              left: hoveredBar.x,
              top: hoveredBar.y,
              transform: `translateY(-50%) translateX(${
                hoveredBar.isFlipped ? 'calc(-100% - 0.5rem)' : '0.5rem'
              })`,
            }}
          >
            <p className="font-medium text-black-071336">
              {hoveredBar.row?.label}
            </p>
            <p className="text-[12px]">
              {(hoveredBar.row?.metrics ?? []).map((metric, index) => (
                <span className="text-gray-700" key={metric?.label}>
                  {index > 0 && (
                    <span className="mx-2 text-gray-C5C9D9">|</span>
                  )}
                  {`${metric?.label} : `}
                  <span className="font-mono font-bold tabular-nums">
                    {metric?.value}
                  </span>
                </span>
              ))}
            </p>
          </div>,
          document.body,
        )}
    </div>
  );
};
